import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined'
import {
  calculateAverageActionsForSecurityControl,
  calculateLateRateForSecurityControls,
  getRisksAndIncidentsBySecurityControl,
  getIntersectionByPerimetreForSecurityControl,
  securityControlMatrix,
} from '../../redux/securityCheckFamily/securityCheckFamilyAction'
import {
  calculateSecurityControlActionsCosts,
  calculateAverageCostForSecurityControl,
  getSecurityControlById,
  fetchCountOtherActionsForSecurityControl,
  fetchCountInProgressActionsForSecurityControl,
  fetchCountDoneActionsForSecurityControl,
} from '../../redux/securityControls/securityControlsAction'
import { RootState, AppDispatch, useAppDispatch } from '../../redux/store'
import MatrixComponent from '../../components/common/MatrixTable'
import {
  selectSecurityControl,
  selectCountOtherActionsForSecurityControl,
  selectCountInProgressActionsForSecurityControl,
  selectCountDoneActionsForSecurityControl,
} from '../../redux/securityControls/securityControlsSelector'
import { Incident } from '../../types/incidentTypes'
import { Risque } from '../../types/risqueTypes'
import {
  AnalysisPdfButton,
  useAnalysisPdfExport,
} from '../../components/common/AnalysisPdfButton'
import AnalysisPdfReport from '../../Pdf/AnalysisPdfReport'
import { selectSpecifications } from '../../redux/specification/specificationSelectors'
import { formatCurrency, locale, formatDecimal } from '../../utils/formatNumbers'
import {
  AnalysisKpiCard,
  AnalysisPageLoader,
  pctTone,
} from '../../components/common/analysisLayout'
import {
  DetailButton,
  DetailPageHead,
} from '../../components/common/detail'
import '../../components/common/maturity-analysis.css'

function SecurityCheckFamilyAnalysis() {
  const { t, i18n } = useTranslation()
  const actionsCost = useSelector(
    (state: RootState) =>
      state.securitycheckFamily.SecurityCheckFamilyActionsCosts?.totalCost
  )
  const securityControl = useSelector(selectSecurityControl)
  const countOtherActions = useSelector(
    selectCountOtherActionsForSecurityControl
  )
  const countInProgressActions = useSelector(
    selectCountInProgressActionsForSecurityControl
  )
  const countDoneActions = useSelector(selectCountDoneActionsForSecurityControl)
  const getTranslatedKey = (key: 'category' | 'name' | 'description') =>
    t(`charts.matrix.headers.${key}`)
  const averageActionsForThisControl = useSelector(
    (state: RootState) =>
      state?.securitycheckFamily?.AverageActionsForSecurityControl?.data
        ?.AverageActionsForThisControl
  )
  const lateRate = useSelector(
    (state: RootState) => state.securitycheckFamily.LateRateForSecurityControls
  )
  const { RisksAndIncidentsBySecurityControl } = useSelector(
    (state: any) => state.securitycheckFamily
  )
  const risks =
    RisksAndIncidentsBySecurityControl?.securityControlDetails?.risks || []
  const incidents =
    RisksAndIncidentsBySecurityControl?.securityControlDetails?.incidents || []

  const matrixData = []
  if (risks.length > 0) {
    matrixData.push(
      {
        [getTranslatedKey('category')]: t('charts.matrix.categories.risk'),
        [getTranslatedKey('name')]: risks[0].RisqueName,
        [getTranslatedKey('description')]: risks[0].Description,
      },
      ...risks.slice(1).map((risk: Risque) => ({
        [getTranslatedKey('category')]: '',
        [getTranslatedKey('name')]: risk.RisqueName,
        [getTranslatedKey('description')]: risk.Description,
      }))
    )
  } else {
    matrixData.push({
      [getTranslatedKey('category')]: t('charts.matrix.categories.risk'),
      [getTranslatedKey('name')]: t('charts.matrix.noData.risk'),
      [getTranslatedKey('description')]: '',
    })
  }

  if (incidents.length > 0) {
    matrixData.push(
      {
        [getTranslatedKey('category')]: t('charts.matrix.categories.incident'),
        [getTranslatedKey('name')]: incidents[0].IncidentName,
        [getTranslatedKey('description')]: incidents[0].Description,
      },
      ...incidents.slice(1).map((incident: Incident) => ({
        [getTranslatedKey('category')]: '',
        [getTranslatedKey('name')]: incident.IncidentName,
        [getTranslatedKey('description')]: incident.Description,
      }))
    )
  } else {
    matrixData.push({
      [getTranslatedKey('category')]: t('charts.matrix.categories.incident'),
      [getTranslatedKey('name')]: t('charts.matrix.noData.incident'),
      [getTranslatedKey('description')]: '',
    })
  }

  const headers = [
    t('charts.matrix.headers.category'),
    t('charts.matrix.headers.name'),
    t('charts.matrix.headers.description'),
  ]
  const dispatch: AppDispatch = useAppDispatch()
  const { securityControlData } = useSelector(
    (state: RootState) => state.security
  )
  const navigate = useNavigate()

  const { id } = useParams<{ id: string }>()
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (!id) {
        setLoading(false)
        return
      }
      setLoading(true)
      try {
        await Promise.all([
          dispatch(getSecurityControlById(id)),
          dispatch(calculateAverageActionsForSecurityControl(id)),
          dispatch(calculateSecurityControlActionsCosts(id)),
          dispatch(calculateAverageCostForSecurityControl(id || '')),
          dispatch(calculateLateRateForSecurityControls(id)),
          dispatch(getRisksAndIncidentsBySecurityControl(id || '')),
          dispatch(getIntersectionByPerimetreForSecurityControl(id)),
          dispatch(securityControlMatrix()),
          dispatch(fetchCountOtherActionsForSecurityControl(id)),
          dispatch(fetchCountInProgressActionsForSecurityControl(id)),
          dispatch(fetchCountDoneActionsForSecurityControl(id)),
        ])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [dispatch, id])

  const { securityControlM } = useSelector(
    (state: RootState) => state.securitycheckFamily
  )

  const dataKeys = {
    securityControl: 'charts.securityControlMatrix.headers.securityControl',
    existingControls: 'charts.securityControlMatrix.headers.existingControls',
    subDomainDescription:
      'charts.securityControlMatrix.headers.subDomainDescription',
    weight: 'charts.securityControlMatrix.headers.weight',
    targetScore: 'charts.securityControlMatrix.headers.targetScore',
    remainingActionsRatio:
      'charts.securityControlMatrix.headers.remainingActionsRatio',
  }

  const matrixDataSecurity = (securityControlM?.matrix || []).map(
    (control: any) => {
      const subDomainCount = control?.SubDomains?.length || 0
      const firstSubDomain = control?.SubDomains?.[0]

      return {
        [t(dataKeys.securityControl)]: control?.Label || '',
        [t(dataKeys.existingControls)]:
          control?.ExistingControls?.length > 0 ? (
            <div>
              {control.ExistingControls.map(
                (existingControl: any, index: number) => (
                  <span key={index}>
                    {`${existingControl?.Label || ''} - ${existingControl?.Perimetre || ''}`}
                  </span>
                )
              )}
            </div>
          ) : (
            t('charts.securityControlMatrix.noData.existingControls')
          ),
        [t(dataKeys.subDomainDescription)]:
          subDomainCount > 0
            ? firstSubDomain?.Description
            : t('charts.securityControlMatrix.noData.subDomains'),
        [t(dataKeys.weight)]: subDomainCount > 0 ? firstSubDomain?.Weight : '',
        [t(dataKeys.targetScore)]:
          subDomainCount > 0 ? firstSubDomain?.TargetScore : '',
        [t(dataKeys.remainingActionsRatio)]:
          control?.RemainingActionsRatio || '',
      }
    }
  )

  const pdfSecurityRows: string[][] = (securityControlM?.matrix || [])
    .filter(
      (control: any) =>
        control?.SecurityControlId === id ||
        (!!securityControl?.SecurityControlId &&
          control?.SecurityControlId === securityControl.SecurityControlId)
    )
    .map((control: any) => {
      const firstSubDomain = control?.SubDomains?.[0]
      const existing = (control?.ExistingControls || [])
        .map(
          (ec: any) => `${ec?.Label || ''} - ${ec?.Perimetre || ''}`
        )
        .join(', ')
      return [
        control?.Label || '',
        existing || t('charts.securityControlMatrix.noData.existingControls'),
        firstSubDomain
          ? firstSubDomain.Description || ''
          : t('charts.securityControlMatrix.noData.subDomains'),
        firstSubDomain ? String(firstSubDomain.Weight ?? '') : '',
        firstSubDomain ? String(firstSubDomain.TargetScore ?? '') : '',
        String(control?.RemainingActionsRatio || ''),
      ]
    })

  const headersSecurity = [
    t(dataKeys.securityControl),
    t(dataKeys.existingControls),
    t(dataKeys.subDomainDescription),
    t(dataKeys.weight),
    t(dataKeys.targetScore),
    t(dataKeys.remainingActionsRatio),
  ]

  const numericLateRate =
    typeof lateRate?.data?.LateRate === 'string'
      ? parseFloat(lateRate.data.LateRate.replace('%', '')) || 0
      : lateRate?.data?.LateRate || 0
  const pdf = useAnalysisPdfExport()
  const currency = useSelector(selectSpecifications)[0]?.Currency

  const computedOn = useMemo(
    () =>
      new Date().toLocaleDateString(i18n.language, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
    [i18n.language]
  )

  const stampTitle = t('securityControlAnalysisPage.computedOn', {
    date: computedOn,
  })
  const backPath = id
    ? `/security-control-details/${id}`
    : '/security-controls'

  const fmtCost = (value: number | undefined | null) =>
    value === undefined || value === null
      ? '—'
      : formatCurrency(Number(value) || 0, currency || '', {
          locale,
          useIntl: true,
        })

  const avgActionsPct = Math.min(
    100,
    Math.max(0, Number(averageActionsForThisControl) * 100 || 0)
  )
  const latePct = Math.min(100, Math.max(0, Number(numericLateRate) || 0))
  const currentScore = Number(securityControl?.SCScore)
  const scoreDisplay = Number.isFinite(currentScore)
    ? formatDecimal(currentScore)
    : '—'

  return (
    <div className="maturity-analysis-page detail-page">
      <DetailPageHead
        eyebrow={t('securityControlAnalysisPage.eyebrow')}
        title={t('securityControlAnalysisPage.pageTitle')}
        subtitle={t('securityControlAnalysisPage.subtitle', {
          name: securityControl?.Label || '—',
          date: computedOn,
        })}
        actions={
          <>
            <DetailButton variant="ghost" onClick={() => navigate(backPath)}>
              <ArrowBackOutlined sx={{ fontSize: 16 }} />
              {t('securityControlAnalysisPage.backToControl')}
            </DetailButton>
            <AnalysisPdfButton
              exporting={pdf.exporting}
              disabled={loading}
              onExport={() =>
                pdf.exportPdf(
                  securityControl?.Label || 'security-control-analysis'
                )
              }
            />
          </>
        }
      />

      {loading ? (
        <AnalysisPageLoader kpiCount={3} chartCount={2} />
      ) : (
        <>
      <div className="ma-kpi-grid ma-kpi-grid--3">
        <AnalysisKpiCard
          label={t('charts.securityActionsCostCard.title')}
          value={fmtCost(actionsCost as number)}
          tone={Number(actionsCost) > 0 ? 'teal' : 'muted'}
          stampTitle={stampTitle}
          description={t('securityControlAnalysisPage.actionsCostDesc')}
        />
        <AnalysisKpiCard
          label={t('charts.averageActionsCard.title')}
          value={formatDecimal(avgActionsPct)}
          suffix="%"
          tone={pctTone(avgActionsPct)}
          progress={avgActionsPct}
          stampTitle={stampTitle}
          description={t('securityControlAnalysisPage.averageActionsDesc')}
        />
        <AnalysisKpiCard
          label={t('charts.totalCostCard.title')}
          value={fmtCost(securityControlData?.TotalCost as number)}
          tone={Number(securityControlData?.TotalCost) > 0 ? 'teal' : 'muted'}
          stampTitle={stampTitle}
          description={t('securityControlAnalysisPage.totalCostDesc')}
        />
      </div>

      <div className="ma-kpi-grid ma-kpi-grid--3">
        <AnalysisKpiCard
          label={t('charts.averageCostPerActionCard.title')}
          value={fmtCost(securityControlData?.AverageCostPerAction as number)}
          tone={
            Number(securityControlData?.AverageCostPerAction) > 0
              ? 'teal'
              : 'muted'
          }
          stampTitle={stampTitle}
        />
        <AnalysisKpiCard
          label={t('charts.totalActionsCard.title')}
          value={securityControlData?.TotalActions ?? '—'}
          tone={
            Number(securityControlData?.TotalActions) > 0 ? 'teal' : 'muted'
          }
          stampTitle={stampTitle}
        />
        <AnalysisKpiCard
          label={t('charts.lateRateCard.title')}
          value={formatDecimal(latePct)}
          suffix="%"
          tone={pctTone(latePct)}
          progress={latePct}
          stampTitle={stampTitle}
          description={t('securityControlAnalysisPage.lateRateDesc')}
        />
      </div>

      <div className="ma-kpi-grid ma-kpi-grid--3">
        <AnalysisKpiCard
          label={t('charts.otherActionsCard.title')}
          value={countOtherActions?.OtherActionsCount ?? '—'}
          tone={
            Number(countOtherActions?.OtherActionsCount) > 0 ? 'teal' : 'muted'
          }
          stampTitle={stampTitle}
        />
        <AnalysisKpiCard
          label={t('charts.inProgressActionsCard.title')}
          value={countInProgressActions?.InProgressActionsCount ?? '—'}
          tone={
            Number(countInProgressActions?.InProgressActionsCount) > 0
              ? 'teal'
              : 'muted'
          }
          stampTitle={stampTitle}
        />
        <AnalysisKpiCard
          label={t('charts.doneActionsCard.title')}
          value={countDoneActions?.DoneActionsCount ?? '—'}
          tone={
            Number(countDoneActions?.DoneActionsCount) > 0 ? 'teal' : 'muted'
          }
          stampTitle={stampTitle}
        />
      </div>

      <div className="ma-kpi-grid">
        <AnalysisKpiCard
          label={t('charts.currentScoreCard.title')}
          value={scoreDisplay}
          tone={currentScore > 0 ? 'teal' : 'muted'}
          stampTitle={stampTitle}
          description={t('securityControlAnalysisPage.currentScoreDesc')}
        />
      </div>

      <p className="ma-section-label">
        {t('securityControlAnalysisPage.sectionRisksIncidents')}
      </p>
      <div className="ma-card">
        <div className="ma-card-head">
          <h3>{t('charts.matrix.title')}</h3>
        </div>
        <div className="ma-card-body ma-card-body--table">
          <MatrixComponent
            variant="analysis"
            title={t('charts.securityControlMatrix.cardTitle')}
            data={matrixData}
            headers={headers}
          />
        </div>
      </div>

      <p className="ma-section-label">
        {t('securityControlAnalysisPage.sectionControlMatrix')}
      </p>
      <div className="ma-card">
        <div className="ma-card-head">
          <h3>{t('charts.securityControlMatrix.title')}</h3>
        </div>
        <div className="ma-card-body ma-card-body--table">
          <MatrixComponent
            variant="analysis"
            title={t('charts.securityControlMatrix.title')}
            data={matrixDataSecurity}
            headers={headersSecurity}
          />
        </div>
      </div>
        </>
      )}

      <div className="ma-pdf-print-host" aria-hidden>
        <AnalysisPdfReport
          ref={pdf.ref}
          eyebrow={t('securityControlAnalysisPage.eyebrow')}
          title={securityControl?.Label || '—'}
          subtitle={t('securityControlAnalysisPage.subtitle', {
            name: securityControl?.Label || '—',
            date: computedOn,
          })}
          footerTitle={t('securityControlAnalysisPage.pageTitle')}
          computedOn={computedOn}
          kpis={[
            {
              label: t('charts.securityActionsCostCard.title'),
              value: fmtCost(actionsCost as number),
              desc: t('securityControlAnalysisPage.actionsCostDesc'),
              tone: Number(actionsCost) > 0 ? 'teal' : 'muted',
            },
            {
              label: t('charts.averageActionsCard.title'),
              value: `${formatDecimal(avgActionsPct)} %`,
              desc: t('securityControlAnalysisPage.averageActionsDesc'),
              tone: pctTone(avgActionsPct),
              progress: avgActionsPct,
            },
            {
              label: t('charts.totalCostCard.title'),
              value: fmtCost(securityControlData?.TotalCost as number),
              desc: t('securityControlAnalysisPage.totalCostDesc'),
              tone: Number(securityControlData?.TotalCost) > 0 ? 'teal' : 'muted',
            },
            {
              label: t('charts.averageCostPerActionCard.title'),
              value: fmtCost(securityControlData?.AverageCostPerAction as number),
              tone:
                Number(securityControlData?.AverageCostPerAction) > 0
                  ? 'teal'
                  : 'muted',
            },
            {
              label: t('charts.totalActionsCard.title'),
              value: String(securityControlData?.TotalActions ?? '—'),
              tone:
                Number(securityControlData?.TotalActions) > 0 ? 'teal' : 'muted',
            },
            {
              label: t('charts.lateRateCard.title'),
              value: `${formatDecimal(latePct)} %`,
              desc: t('securityControlAnalysisPage.lateRateDesc'),
              tone: pctTone(latePct),
              progress: latePct,
            },
            {
              label: t('charts.otherActionsCard.title'),
              value: String(countOtherActions?.OtherActionsCount ?? '—'),
              tone:
                Number(countOtherActions?.OtherActionsCount) > 0
                  ? 'teal'
                  : 'muted',
            },
            {
              label: t('charts.inProgressActionsCard.title'),
              value: String(
                countInProgressActions?.InProgressActionsCount ?? '—'
              ),
              tone:
                Number(countInProgressActions?.InProgressActionsCount) > 0
                  ? 'teal'
                  : 'muted',
            },
            {
              label: t('charts.doneActionsCard.title'),
              value: String(countDoneActions?.DoneActionsCount ?? '—'),
              tone:
                Number(countDoneActions?.DoneActionsCount) > 0
                  ? 'teal'
                  : 'muted',
            },
            {
              label: t('charts.currentScoreCard.title'),
              value: scoreDisplay,
              desc: t('securityControlAnalysisPage.currentScoreDesc'),
              tone: currentScore > 0 ? 'teal' : 'muted',
            },
          ]}
          tables={[
            {
              label: t('securityControlAnalysisPage.sectionRisksIncidents'),
              title: t('charts.matrix.title'),
              headers,
              rows: matrixData.map((row) =>
                headers.map((h) => String(row[h] ?? ''))
              ),
              columns: 'minmax(0, 0.8fr) minmax(0, 1.4fr) minmax(0, 2fr)',
            },
            {
              label: t('securityControlAnalysisPage.sectionControlMatrix'),
              title: t('charts.securityControlMatrix.title'),
              headers: headersSecurity,
              rows: pdfSecurityRows,
              columns:
                'minmax(0, 1.3fr) minmax(0, 1.1fr) minmax(0, 2.6fr) minmax(0, 0.6fr) minmax(0, 0.7fr) minmax(0, 0.9fr)',
            },
          ]}
        />
      </div>
    </div>
  )
}

export default SecurityCheckFamilyAnalysis
